import { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Text, chakra } from '@enechain/ecloud-designsystem'

import type { KomaSlot, KomaSlotState } from './types'

type Props = {
  /** 1〜48 のコマ slot 配列 (= 48 件固定想定)。 */
  slots: KomaSlot[]
  /** 「現在」 ハイライト対象の slot index (= 1-48)。 */
  currentSlotIndex: number
  /** GC 該当の slot index (= 1-48)。 intraday で利用。 */
  gcSlotIndex?: number
  /** ユーザーがクリックで選択している slot index。 */
  selectedSlotIndex?: number
  /** chip クリック時に呼ばれる。 gc-closed 状態の chip は disabled で発火しない。 */
  onSlotClick?: (index: number) => void
  /** mount 時 / currentSlotIndex 更新時に current chip を中央寄せ scroll する (default true)。 */
  autoScrollToCurrent?: boolean
}

const stateClass = (state: KomaSlotState): string => {
  switch (state) {
    case 'pre':
      return 'upcoming'
    case 'pre-acceptance':
      return 'pre-acceptance'
    case 'open':
      return 'trading'
    case 'gc-active':
      return 'gc-active'
    case 'gc-closed':
      return 'gc-closed'
    case 'matched':
      return 'matched'
    case 'completed':
      return 'gc-closed'
  }
}

/**
 * design HTML `components.css:2953-2978` 準拠の chip 下線色解決。
 * 優先順位: violation (negative) > inversion (warning) > state-derived (open/matched/pre)。
 *
 * このファイル全体の red.500 / orange.500 / green.400 / green.600 / blue 系の値は
 * コマ状態 (open/matched/gc-active/selected/hasBid 等) を多段階で視覚的に区別する
 * データ可視化状態色であり、 単一の semantic 役割 (positive/negative/info 等) に
 * 一意対応しないため chakra パレット容認 (2026-07-05 ユーザー判断)。 DS に
 * この粒度の状態色 token は無い。
 */
const borderBottomColor = (
  state: KomaSlotState,
  hasViolation: boolean,
  hasInversion: boolean,
): string | undefined => {
  if (hasViolation) return 'red.500'
  if (hasInversion) return 'orange.500'
  switch (state) {
    case 'open':
      return 'green.400'
    case 'matched':
      return 'green.600'
    // 受付前は未開場 ('pre') の見た目を流用し、 ラベルと活性だけを変える
    // (色・強調はデザイナー判断範囲なので新しい配色を起こさない)。
    case 'pre':
    case 'pre-acceptance':
      return 'Border.midEmphasis'
    default:
      return undefined
  }
}

/**
 * design HTML `.koma-chip.spot-has-bid` を含めた bg 解決。
 * 優先順位: selected > gc-active > gc-closed/completed > spot-has-bid > 通常 (white)。
 */
const stateBg = (
  state: KomaSlotState,
  isSelected: boolean,
  hasBid: boolean,
): string => {
  if (isSelected) return 'blue.500'
  switch (state) {
    case 'gc-closed':
    case 'completed':
      return 'Surface.tertiary'
    case 'gc-active':
      return 'orange.50'
    default:
      return hasBid ? 'blue.50' : 'Surface.primary'
  }
}

const stateColor = (
  state: KomaSlotState,
  isSelected: boolean,
  hasBid: boolean,
): string => {
  if (isSelected) return 'Text.highEmphasisInverse'
  switch (state) {
    case 'gc-closed':
    case 'completed':
      // gray.400 (#a1a1aa) は disabled/GC 終了後の chip 文字色用途のため Text.disabled を採用。
      return 'Text.disabled'
    case 'gc-active':
      return 'orange.700'
    default:
      return hasBid ? 'blue.700' : 'Text.highEmphasis'
  }
}

/**
 * 48 コマを横方向に並べる sticky ribbon。
 * design HTML: `design/bidding/intraday.html` の `.koma-ribbon-wrap` `.koma-ribbon` `.koma-chip`
 * に対応 (= eclair design tokens の値をベースに Chakra 色トークンへマップ)。
 *
 * - props 駆動 (= 内部 state なし、 親が KomaSlot[] を組み立てて渡す)
 * - autoScrollToCurrent で currentSlotIndex を中央寄せ
 * - クリックは gc-closed / completed では発火しない (= disabled)
 */
export const KomaRibbon = ({
  slots,
  currentSlotIndex,
  gcSlotIndex,
  selectedSlotIndex,
  onSlotClick,
  autoScrollToCurrent = true,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const wrapRef = useRef<HTMLDivElement>(null)
  const currentChipRef = useRef<HTMLButtonElement>(null)

  const labelByState = useMemo<Record<KomaSlotState, string>>(
    () => ({
      pre: t('page:biddingCommon.ribbon.pre'),
      'pre-acceptance': t('page:biddingCommon.ribbon.preAcceptance'),
      open: t('page:biddingCommon.ribbon.current'),
      'gc-active': t('page:biddingCommon.ribbon.current'),
      'gc-closed': t('page:biddingCommon.ribbon.gcDone'),
      matched: t('page:biddingCommon.ribbon.matched'),
      completed: t('page:biddingCommon.ribbon.completed'),
    }),
    [t],
  )

  useEffect(() => {
    if (!autoScrollToCurrent) return
    const wrap = wrapRef.current
    const chip = currentChipRef.current
    if (wrap === null || chip === null) return
    const wrapRect = wrap.getBoundingClientRect()
    const chipRect = chip.getBoundingClientRect()
    const offset =
      chip.offsetLeft -
      wrap.offsetLeft -
      wrapRect.width / 2 +
      chipRect.width / 2
    wrap.scrollTo({ left: offset, behavior: 'smooth' })
  }, [autoScrollToCurrent, currentSlotIndex, slots])

  return (
    <Box
      ref={wrapRef}
      overflowX="auto"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      aria-label={t('page:biddingCommon.ribbon.ariaLabel')}
      backgroundColor="Surface.secondary"
    >
      <Flex gap="2px" minWidth="max-content" padding="4px 8px" role="toolbar">
        {slots.map((slot) => {
          const isCurrent = slot.index === currentSlotIndex
          const isGcActive =
            slot.state === 'gc-active' ||
            (gcSlotIndex !== undefined && slot.index === gcSlotIndex)
          const isSelected =
            selectedSlotIndex !== undefined && slot.index === selectedSlotIndex
          // 受付前 (AC-BIN-089c) は札作成モーダルを開かせないので GC 済と
          // 同じく非活性にする (= クリック不可 + 既存の disabled 表現)。
          const isDisabled =
            slot.state === 'gc-closed' ||
            slot.state === 'completed' ||
            slot.state === 'pre-acceptance'
          const stateForLabel = isGcActive ? 'gc-active' : slot.state
          const hasBid = slot.hasBid === true
          const hasViolation = slot.hasViolation === true
          const hasInversion = slot.hasInversion === true
          const borderColor = borderBottomColor(
            slot.state,
            hasViolation,
            hasInversion,
          )
          // tooltip 末尾に violation / inversion 注記を付与 (= design `.koma-chip` の title 属性と同パターン)
          const statusSuffix = hasViolation
            ? ' / 制限違反'
            : hasInversion
              ? ' / 売買逆転'
              : ''
          const tooltip = `${t('page:biddingCommon.ribbon.slotPrefix', {
            index: slot.index,
          })} / ${slot.time} / ${labelByState[stateForLabel]}${
            slot.count !== undefined && slot.count > 0
              ? ` / ${slot.count}件`
              : ''
          }${statusSuffix}`
          return (
            <chakra.button
              key={slot.index}
              ref={isCurrent ? currentChipRef : undefined}
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              display="flex"
              minWidth="44px"
              padding="2px 6px"
              color={stateColor(
                isGcActive ? 'gc-active' : slot.state,
                isSelected,
                hasBid,
              )}
              fontWeight={
                isCurrent || isGcActive || isSelected || hasBid
                  ? 'bold'
                  : 'medium'
              }
              lineHeight={1.2}
              opacity={isDisabled ? 0.5 : 1}
              borderWidth="1px"
              borderColor={
                isSelected
                  ? 'blue.500'
                  : hasBid
                    ? 'blue.300'
                    : 'Border.lowEmphasis'
              }
              borderBottom={
                borderColor !== undefined
                  ? hasViolation || hasInversion
                    ? '3px solid'
                    : '2px solid'
                  : undefined
              }
              borderBottomColor={borderColor}
              borderRadius="md"
              _hover={
                isDisabled
                  ? undefined
                  : {
                      backgroundColor: isSelected
                        ? 'blue.600'
                        : 'Surface.tertiary',
                    }
              }
              cursor={isDisabled ? 'not-allowed' : 'pointer'}
              transition="background 0.15s, border-color 0.15s"
              aria-label={tooltip}
              backgroundColor={stateBg(
                isGcActive ? 'gc-active' : slot.state,
                isSelected,
                hasBid,
              )}
              data-current={isCurrent}
              data-gc-active={isGcActive}
              data-has-bid={hasBid}
              data-has-inversion={hasInversion}
              data-has-violation={hasViolation}
              data-selected={isSelected}
              data-state={stateClass(slot.state)}
              disabled={isDisabled}
              onClick={() => {
                if (isDisabled) return
                onSlotClick?.(slot.index)
              }}
              title={tooltip}
              type="button"
            >
              <Text
                textStyle="captionStrong"
                css={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {slot.time}
              </Text>
              {slot.count !== undefined && slot.count > 0 ? (
                <Text marginTop="1px" fontSize="xs">
                  {slot.count}
                </Text>
              ) : (
                <Box fontSize="xs" visibility="hidden">
                  0
                </Box>
              )}
            </chakra.button>
          )
        })}
      </Flex>
    </Box>
  )
}
